import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '../lib/api'

interface User {
  id: string
  email: string
  name?: string
  tier: 'free' | 'pro' | 'super'
  analysesThisMonth: number
  alpacaMode: 'paper' | 'live'
}

// Private single-user system: the one account is always the full-access ("super") user. We coerce
// tier here so the app never shows SaaS upgrade CTAs / tier gates, regardless of the stored value.
// The `tier` field is kept on the type only so the backend contract doesn't break. De-SaaS v3.55.0.
function asSuper(user: User | null): User | null {
  return user ? { ...user, tier: 'super' } : user
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  login:   (email: string, password: string) => Promise<void>
  signup:  (email: string, password: string, name?: string) => Promise<void>
  logout:  () => void
  refresh: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user:      null,
      token:     null,
      isLoading: false,

      login: async (email, password) => {
        set({ isLoading: true })
        try {
          const { token, user } = await api.auth.login({ email, password })
          localStorage.setItem('polywally_token', token)
          set({ token, user: asSuper(user), isLoading: false })
        } catch (err) {
          set({ isLoading: false })
          throw err
        }
      },

      signup: async (email, password, name) => {
        set({ isLoading: true })
        try {
          const { token, user } = await api.auth.signup({ email, password, name })
          localStorage.setItem('polywally_token', token)
          set({ token, user: asSuper(user), isLoading: false })
        } catch (err) {
          set({ isLoading: false })
          throw err
        }
      },

      logout: () => {
        localStorage.removeItem('polywally_token')
        set({ user: null, token: null })
      },

      refresh: async () => {
        try {
          const user = await api.auth.me()
          set({ user: asSuper(user) })
        } catch {
          get().logout()
        }
      },
    }),
    {
      name: 'polywally-auth',
      partialize: (state) => ({ token: state.token, user: state.user }),
    }
  )
)
